import { copyTextToClipboard } from './copyToClipboard';
import { buildInviteShareUrl } from './routing';
import type { LscLeagueInvite } from './types';

export const INVITE_COPIED_MESSAGE = 'Invite copied';
export const INVITE_COPY_FAILED_MESSAGE = 'Could not copy. Select and copy manually.';
export const INVITE_SHARED_MESSAGE = 'Invite shared';

/** Resolve a commissioner-shareable invite URL from API fields or the current origin. */
export function resolveInviteShareUrl(
  invite: Pick<LscLeagueInvite, 'code' | 'invite_url' | 'share_path'>,
  hostname?: string
): string | null {
  const inviteUrl = invite.invite_url?.trim();
  if (inviteUrl) {
    return inviteUrl;
  }

  const code = invite.code?.trim();
  if (!code) {
    return null;
  }

  return buildInviteShareUrl(code, hostname);
}

export function buildInviteClipboardText(opts: {
  url: string;
  code: string;
  leagueName?: string;
}): string {
  const leagueLabel = opts.leagueName?.trim() || 'this league';
  return [
    `Join ${leagueLabel} on Scoutlytics Stream Center:`,
    opts.url,
    '',
    'Invite code:',
    opts.code,
  ].join('\n');
}

export function buildInviteShareText(opts: {
  url: string;
  code: string;
  leagueName?: string;
}): string {
  return buildInviteClipboardText(opts);
}

export function canUseNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export async function shareInviteLink(opts: {
  url: string;
  code: string;
  leagueName?: string;
}): Promise<'shared' | 'copied' | 'failed' | 'cancelled'> {
  const { url, code, leagueName } = opts;
  const title = leagueName ? `Join ${leagueName}` : 'Join league on Stream Center';
  const text = buildInviteShareText({ url, code, leagueName });

  if (canUseNativeShare()) {
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return 'cancelled';
      }
    }
  }

  return (await copyTextToClipboard(text)) ? 'copied' : 'failed';
}
